import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import _ from 'lodash';

import translationKeys from '../../../translations/keys';

import useWhiteLabelComponent from '../../../hooks/useWhiteLabelComponent';
import useKeyboardZoomControls from '../../../hooks/useKeyboardZoomControls';
import useResizeOberserver from '../../../hooks/useResizeObserver';

import { getMediaSnapshot } from '../../../utils/video';

import { ReactComponent as CloseIcon } from '../../../assets/images/icons/close.svg';
import { ReactComponent as Play } from '../../../assets/images/icons/play.svg';

import { VideoPlayerWithControls } from '../../video-player';
import Loading from '../../loading';
import P from '../../p';
import { ModalWrapper } from '../Modal';
import { ZoomableContent, ZoomableWrapper, ZoomControls } from '../../zoomable-wrapper';
import {
    ModalPlayerContainer,
    ModalPlayerHeader,
    ModalPlayerTitle,
    ModalPlayerCloseButton,
    ModalPlayerFooter,
    ContentWrapper,
    ImageElement,
    ModalPlayerFooterContent,
    ModalPlayerFooterLeft,
    ModalPlayerFooterActions,
    VideoContainer,
    MediaPlaceholder,
    MediaErrorMessageContainer,
    ZoomableImageContainer,
    ContentTitle,
    ContentFooter,
    ContentContainer,
    ModalPlayerVehicleAnalytics,
} from './ModalPlayer.styles';
import useSwipeGesture from '../../../hooks/useSwipeGesture';
import IconButton from '../../button/icon';
import { useMobileLayout } from '../../../contexts/mobile-context/MobileContext';
import AnalyticsOverlay from '../../analytics-overlay';

/** Modal for playing a video or displaying an image. Content for footer is passed through children. */
const ModalPlayer = withTranslation()(
    ({
        show = false,
        closeModal,
        loading = false,
        error,
        mediaLoading = false,
        mediaError,
        headerText,
        contentTitle,
        contentFooter,
        videoConfig,
        imageConfig,
        children,
        closeRef,
        containerRef,
        showPrevious,
        showNext,
        scrollableContent,
        thumbs,
        analyticsOverlayData,
        previousAnalyticsOverlayData,
        nextAnalyticsOverlayData,
        t,
        ...props
    }) => {
        const { isMobileLayout } = useMobileLayout();
        const [showVideo, setShowVideo] = useState(!isMobileLayout);
        const [thumbOpacity, setThumbOpacity] = useState(1);
        
        // State for managing slide transitions
        const [isTransitioning, setIsTransitioning] = useState(false);
        const [transitionDirection, setTransitionDirection] = useState(null); // 'left' or 'right'
        const [currentThumb, setCurrentThumb] = useState(thumbs?.current);

        // Refs for transition animation frames to enable cleanup
        const rafIdRef = useRef(null);
        const innerRafIdRef = useRef(null);

        useEffect(() => {
            if (!isTransitioning && thumbs?.current) {
                setCurrentThumb(thumbs.current);
                setThumbOpacity(1);
            }
        }, [isTransitioning, thumbs]);
        useEffect(() => {
            if (_.isNil(videoConfig) && !_.isNil(imageConfig)) {
                setShowVideo(true);
            } else if (_.isNil(imageConfig) && !_.isNil(videoConfig)) {
                setShowVideo(true);
            }

        }, [imageConfig, videoConfig]);

        useEffect(() => {
            if (!isMobileLayout || _.isNil(thumbs)) {
                setShowVideo(true);
            }
        }, [isMobileLayout, thumbs]);

        const handleShowPrevious = useCallback(() => {
            if (!showPrevious || !thumbs?.previous || isTransitioning) return;

            // Clear any existing animation frames
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
            if (innerRafIdRef.current) cancelAnimationFrame(innerRafIdRef.current);

            setShowVideo(false);
            setTransitionDirection('left');
            setThumbOpacity(1);

            rafIdRef.current = requestAnimationFrame(() => {
                setIsTransitioning(true);

                innerRafIdRef.current = requestAnimationFrame(() => {
                    // Use setTimeout for the actual duration since RAF doesn't provide delays
                    const timeoutId = setTimeout(() => {
                        setCurrentThumb(thumbs.previous);
                        setIsTransitioning(false);
                        setTransitionDirection(null);
                        showPrevious();
                    }, 300);

                    // Store timeout ID for cleanup
                    innerRafIdRef.current = timeoutId;
                });
            });
        }, [showPrevious, thumbs, isTransitioning]);

        const handleShowNext = useCallback(() => {
            if (!showNext || !thumbs?.next || isTransitioning) return;

            // Clear any existing animation frames
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
            if (innerRafIdRef.current) cancelAnimationFrame(innerRafIdRef.current);

            setShowVideo(false);
            setTransitionDirection('right');
            setThumbOpacity(1);

            rafIdRef.current = requestAnimationFrame(() => {
                setIsTransitioning(true);

                innerRafIdRef.current = requestAnimationFrame(() => {
                    // Use setTimeout for the actual duration since RAF doesn't provide delays
                    const timeoutId = setTimeout(() => {
                        setCurrentThumb(thumbs.next);
                        setIsTransitioning(false);
                        setTransitionDirection(null);
                        showNext();
                    }, 300);

                    // Store timeout ID for cleanup
                    innerRafIdRef.current = timeoutId;
                });
            });
        }, [showNext, thumbs, isTransitioning]);

        const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwipeGesture(
            handleShowPrevious, 
            handleShowNext, 
            50, 
            ['video-player-controls']
        );

        useEffect(() => {
            if (!show || loading || error) return;
            
            const container = containerRef?.current;
            if (!container) return;
            
            // Use capture phase (third parameter = true) to get events before children
            container.addEventListener('touchstart', handleTouchStart, true);
            container.addEventListener('touchmove', handleTouchMove, true);
            container.addEventListener('touchend', handleTouchEnd, true);
            
            return () => {
                container.removeEventListener('touchstart', handleTouchStart, true);
                container.removeEventListener('touchmove', handleTouchMove, true);
                container.removeEventListener('touchend', handleTouchEnd, true);
            };
        }, [show, loading, error, handleTouchStart, handleTouchMove, handleTouchEnd]);

        // Cleanup transition animation frames and timeouts on unmount
        useEffect(() => {
            return () => {
                if (rafIdRef.current) {
                    // Could be either RAF ID or timeout ID, so try both
                    cancelAnimationFrame(rafIdRef.current);
                    clearTimeout(rafIdRef.current);
                }
                if (innerRafIdRef.current) {
                    // Could be either RAF ID or timeout ID, so try both
                    cancelAnimationFrame(innerRafIdRef.current);
                    clearTimeout(innerRafIdRef.current);
                }
            };
        }, []);

        const { navColour: headerColour, navTextColour: headerTextColour, accentColour: spinnerColour } = useWhiteLabelComponent();

        useEffect(() => {
            // If ModalPlayer is loading when it first opens, useModal hook won't be able to put focus on close button (as it is not mounted)
            // So do that here when it stops loading
            if (show && !loading) {
                closeRef?.current?.focus();
            }
        }, [closeRef, show, loading]);

        const renderMediaPreview = (thumbSrc, isNext = false, opacity = 1) => {
            if (!thumbSrc) return null;
            
            let transform;
            if (isNext) {
                if (!isTransitioning) {
                    transform = transitionDirection === 'left' ? 'translate3d(100%, 0, 0)' : 'translate3d(-100%, 0, 0)';
                } else {
                    transform = 'translate3d(0, 0, 0)';
                }
            } else {
                if (!isTransitioning) {
                    transform = 'translate3d(0, 0, 0)';
                } else {
                    transform = transitionDirection === 'left' ? 'translate3d(-100%, 0, 0)' : 'translate3d(100%, 0, 0)';
                }
            }
            
            const style = {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                transform: transform,
                WebkitTransform: transform,
                transition: 'transform 0.3s ease-out, opacity 1.2s ease-out',
                WebkitTransition: '-webkit-transform 0.3s ease-out, opacity 1.2s ease-out',
                willChange: 'transform, opacity',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
                opacity: opacity,
                zIndex: 2,
                pointerEvents: 'none'
            };

            // Determine which overlay data to use
            let overlayData = analyticsOverlayData;
            if (isNext) {
                // When showing next thumb, use appropriate overlay based on direction
                overlayData = transitionDirection === 'left' ? previousAnalyticsOverlayData : nextAnalyticsOverlayData;
            }

            return (
                <div style={style}>
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%', height: '100%' }}>
                        {/* Hide overlay during transition - SVG filters break animation on iPhone 14+ */}
                        {overlayData?.tracks && !isTransitioning && !transitionDirection && <AnalyticsOverlay {...overlayData} disableFilters />}
                        <img src={thumbSrc} alt="Preview" style={{ width: '100%', height: 'auto' }} />
                        {!isNext && !isTransitioning && opacity > 0 && (
                            <IconButton
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    background: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: '50%',
                                    padding: '8px'
                                }}
                                onClick={() => {
                                    setShowVideo(true);
                                    setThumbOpacity(0);
                                }}
                            >
                                <Play
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        color: 'white'
                                    }}
                                />
                            </IconButton>
                        )}
                    </div>
                </div>
            );
        };

       // Get the next thumb based on transition direction
        const nextThumb = useMemo(() => {
            if (!transitionDirection || !thumbs) return null;
            return transitionDirection === 'left' ? thumbs.previous : thumbs.next;
        }, [transitionDirection, thumbs]);

        return (
            <ModalWrapper
                show={show}
                closeModal={closeModal}
                containerRef={containerRef}
            >
                {error?.length > 0 ? (
                    <P style={{ minWidth: '70vw' }} centre colour="#fff">
                        {error}
                    </P>
                ) : loading ? (
                    <Loading spinnerColour={spinnerColour} />
                ) : (
                    <ModalPlayerContainer
                        $scrollableContent={scrollableContent}
                        {...props}
                    >
                        {/* Header */}
                        <ModalPlayerHeader
                            $backgroundColour={headerColour}
                            $textColour={headerTextColour}
                        >
                            <ModalPlayerTitle>{headerText}</ModalPlayerTitle>
                            <ModalPlayerCloseButton
                                ref={closeRef}
                                aria-label={t(translationKeys.actions.CLOSE)}
                                onClick={closeModal}
                                $colour={headerTextColour}
                            >
                                <CloseIcon />
                            </ModalPlayerCloseButton>
                        </ModalPlayerHeader>

                        <ContentContainer>
                        {contentTitle && <ContentTitle>{contentTitle}</ContentTitle>}
                        {/* Body */}
                        <ContentWrapper>
                            {
                                (mediaLoading || mediaError) ? (
                                    <MediaPlaceholder>
                                        {
                                            (!showVideo && !isMobileLayout) || mediaLoading ? (
                                                    currentThumb && isMobileLayout ? (
                                                        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
                                                            {renderMediaPreview(currentThumb, false, thumbOpacity)}
                                                            {transitionDirection && nextThumb && renderMediaPreview(nextThumb, true, 1)}
                                                        </div>
                                                    ) : (
                                                        <Loading fill style={{ background: 'none' }}/>
                                                    )
                                            ) : (
                                                <MediaErrorMessageContainer>
                                                    {
                                                        typeof mediaError === 'string' ? (
                                                            <p>{mediaError}</p>
                                                        ) : (
                                                            <>
                                                                { mediaError.heading && <h2>{mediaError.heading}</h2> }
                                                                {
                                                                    (Array.isArray(mediaError.text) ? mediaError.text : [mediaError.text]).map((text, index) => (
                                                                        <p key={index}>{text}</p>
                                                                    ))
                                                                }
                                                            </>
                                                        )
                                                    }
                                                </MediaErrorMessageContainer>
                                            )
                                        }
                                    </MediaPlaceholder>
                                ) : videoConfig ? (
                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                        <VideoContainer className="video-container">
                                            <VideoPlayerWithControls
                                                width="100%"
                                                height="100%"
                                                {...videoConfig}
                                                autoPlay={true}
                                                onReady={(player) => {
                                                    setThumbOpacity(0);
                                                    videoConfig?.onReady?.(player);
                                                }}
                                                // TODO: Handle errors with onError prop
                                            />
                                        </VideoContainer>
                                        {currentThumb && isMobileLayout && analyticsOverlayData?.tracks && (
                                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', pointerEvents: thumbOpacity === 0 ? 'none' : 'auto' }}>
                                                {renderMediaPreview(currentThumb, false, thumbOpacity)}
                                                {transitionDirection && nextThumb && renderMediaPreview(nextThumb, true, 1)}
                                            </div>
                                        )}
                                    </div>
                                ) : imageConfig ? (
                                    <ZoomableImage
                                        {...imageConfig}
                                    />
                                ) : null
                            }
                        </ContentWrapper>
                        {contentFooter && <ContentFooter>{contentFooter.map(item => item ? (<div style={{ minHeight: 50 }}>{item}</div>) : null)}</ContentFooter>}
                        </ContentContainer>

                        {/* Footer */}
                        <ModalPlayerFooter>
                            {children}


                            {/* Pagination buttons currently only shown on mobile */}
                            {/* <ModalPlayerPaginationButtonsContainer>
                                <ModalPlayerPaginationButton
                                    onClick={showNext}
                                    disabled={!showNext}
                                    style={{ paddingRight: 14 }} // svgs are padded with some empty space so looks uneven next to each other in buttons
                                >
                                    <PaginationBackIcon />
                                </ModalPlayerPaginationButton>
                                <ModalPlayerPaginationButton
                                    onClick={showPrevious}
                                    disabled={!showPrevious}
                                    style={{ paddingLeft: 14 }} // svgs are padded with some empty space so looks uneven next to each other in buttons
                                >
                                    <PaginationForwardIcon />
                                </ModalPlayerPaginationButton>
                            </ModalPlayerPaginationButtonsContainer> */}
                        </ModalPlayerFooter>
                    </ModalPlayerContainer>
                )}
            </ModalWrapper>
        );
    }
);

// Component for showing images and zooming/rotating them
// As soon as this is needed elsewhere, make it its own standalone component like VideoPlayer
// Images are rendered according to their initial aspect ratio, then rotating occurs within that fixed area
const ZoomableImage = memo(forwardRef(({ zoomControls = false, rotateControls = false, ...props }, ref) => {

    const zoomControlsRef = useRef();
    const containerRef = useRef();
    useKeyboardZoomControls(containerRef, { zoomControlsRef, disabled: !zoomControls });

    // Identify image aspect ratio
    const [[imageWidth, imageHeight], setImageWidthHeight] = useState([]);
    useLayoutEffect(() => {
        const image = new Image();
        image.onload = () => {
            setImageWidthHeight([image.width, image.height]);
        }
        image.src = props.src;
    }, [props.src]);
    
    // Identify container size
    const [[containerWidth, containerHeight], setContainerWidthHeight] = useState([]);
    useResizeOberserver(containerRef, ({ width, height }) => {
        setContainerWidthHeight([width, height]);
    });

    // 0, 90, 180, 270
    const [rotation, setRotation] = useState(0);

    const imageRef = useRef();

    // Control snapshot animation
    const [showSnapshotOverlay, setShowSnapshotOverlay] = useState(false);
    useEffect(() => {
        if (showSnapshotOverlay) {
            // Clear overlay very soon after it's shown to create snapshot animation
            const timeout = setTimeout(() => setShowSnapshotOverlay(false), 200);
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [showSnapshotOverlay]);

    useImperativeHandle(ref, () => {
        return {
            container: containerRef.current,
            takeSnapshot: () => {
                setShowSnapshotOverlay(true);
                const canvas = getMediaSnapshot(
                    imageRef.current,
                    containerRef.current,
                    zoomControlsRef.current.instance.transformState
                );
                return canvas;
            }
        };
    }, []);

    return (
        <ZoomableImageContainer
            ref={containerRef}
            tabIndex={zoomControls ? 0 : undefined}
            $imageWidth={imageWidth}
            $imageHeight={imageHeight}
            $snapshotOverlay={showSnapshotOverlay}
        >
            <ZoomableWrapper
                ref={zoomControlsRef}
                disabled={!zoomControls}
            >
                <ZoomableContent>
                    
                    <ImageElement
                        ref={imageRef}
                        {...props}
                        style={{
                            // On certain screen sizes, we were seeing image rendering smaller than specified
                            // so added min width/height.
                            width: rotation % 180 === 0 ? '100%' : containerHeight,
                            minWidth: rotation % 180 === 0 ? '100%' : containerHeight,
                            height: rotation % 180 === 0 ? '100%' : containerWidth,
                            minHeight: rotation % 180 === 0 ? '100%' : containerWidth,
                            transform: `rotateZ(${rotation}deg)${rotation % 180 === 0 ? '' : ` translate(${rotation >= 180 ? -containerHeight : 0}px, ${rotation % 270 === 0 ? 0 : -containerWidth}px)`}`,
                            transformOrigin: rotation % 180 === 0 ? 'center' : 'top left'
                        }}
                    />
                </ZoomableContent>

                {
                    zoomControls && (
                        <ZoomControls
                            onReset={() => setRotation(0)}
                            onRotate={rotateControls ? () => {
                                setRotation(currentRotation => {
                                    return currentRotation === 0 ? 270 : currentRotation - 90;
                                });
                            } : undefined}
                        />
                    )
                }
            </ZoomableWrapper>
        </ZoomableImageContainer>
    );
}));

ModalPlayer.FooterLeft = ModalPlayerFooterLeft;
ModalPlayer.FooterActions = ModalPlayerFooterActions;
ModalPlayer.FooterContent = ModalPlayerFooterContent;
ModalPlayer.VehicleAnalytics = ModalPlayerVehicleAnalytics;

ModalPlayer.propTypes = {
    /** Show the player. */
    show: PropTypes.bool,
    /** Function to close the player. Called when close icon or background is clicked. */
    closeModal: PropTypes.func.isRequired,
    /** Ref to attach to close button. */
    closeRef: PropTypes.shape({
        current: PropTypes.oneOfType([PropTypes.element, PropTypes.object])
    }),
    /** Ref to attach to container. Use to trap focus within this element. */
    containerRef: PropTypes.shape({
        current: PropTypes.oneOfType([PropTypes.element, PropTypes.object])
    }),
    /** Loading spinner shown instead of modal while this is true. Use this when the modal cannot be shown yet. If you're only waiting for the media to load, use `mediaLoading` instead. */
    loading: PropTypes.bool,
    /** Loading spinner shown instead of video/image. */
    mediaLoading: PropTypes.bool,
    /** Error message shown instead of modal. Use this when the error means you cannot show the modal at all. If only the media has failed, use `mediaError` instead. */
    error: PropTypes.string,
    /** Error message shown instead of video/image. */
    mediaError: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
        heading: PropTypes.string,
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    })]),
    /** Loading component shown instead of modal when `loading` prop is true. */
    Loading: PropTypes.elementType,
    /** Text shown in header. */
    headerText: PropTypes.string,
    /** When using the modal to play a video, this must be an object containing the required props for ReactPlayer. */
    videoConfig: PropTypes.object,
    /** When using the modal to display an image, this must be an object containing required props to pass to <img /> (e.g. src, alt). Can also contain boolean `zoomControls` and `rotateControls` to let the user zoom/pan/rotate the image (`zoomControls` must be true for `rotateControls` to take effect). */
    imageConfig: PropTypes.object,
    /** Content for left side of footer top row. */
    footerLeft: PropTypes.node,
    /** Array of icons for right side of footer top row. */
    actionIcons: PropTypes.arrayOf(PropTypes.node),
    /** Content for bottom of footer. */
    footerContent: PropTypes.node,
    /** On mobile view, render left arrow button and invoke this function when clicked. */
    showPrevious: PropTypes.func,
    /** On mobile view, render right arrow button and invoke this function when clicked. */
    showNext: PropTypes.func,
    /** Used for styling when content would be overflowing (currently vehicles table) - on mobile view, this should be hidden */
    scrollableContent: PropTypes.bool
};

export default ModalPlayer;
